import { clsx } from 'clsx';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import Autosuggest from 'react-autosuggest';
import { TSchool } from 'models/auth';
import StateCityModel from 'models/StateCity';
import styles from './schoolPicker.module.sass';

// @ts-expect-error should be fixed in the future
const AutosuggestComponent = Autosuggest as React.ComponentType<any>;

export const optionRender = (option: TSchool) => (
  <div className={styles.option}>
    <div
      style={{
        fontSize: '14px',
        fontWeight: 'bold',
        textAlign: 'left',
      }}
    >
      {option.name}
      {option.alias && (
        <span style={{ fontStyle: 'italic', fontSize: '13px' }}>
          {' - '}({option.alias})
        </span>
      )}
    </div>
    <div
      style={{
        lineHeight: '1em',
        fontSize: '12px',
        textAlign: 'left',
      }}
    >
      {option.street}, {option.city}, {option.state} - {option.zip}
    </div>
  </div>
);

interface ISchool {
  name: string;
  id: string;
}

interface SchoolPickerProps {
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  stateCode?: string;
  initialSchool?: ISchool;
  autosuggestClassName?: string;
  onChange: (isSchoolSelected: boolean, school: string | TSchool) => void;
  city: string;
  inputClassName?: string;
  fieldName?: string;
  // Called each time the schools are fetched
  onSuggestion?: (schools: TSchool[], search: string) => void;
}

const SchoolPicker: FC<SchoolPickerProps> = ({
  className,
  autosuggestClassName,
  placeholder,
  disabled,
  onBlur,
  stateCode,
  initialSchool,
  onChange,
  city,
  inputClassName,
  fieldName = 'organization',
  onSuggestion,
}) => {
  const [filteredSchools, setFilteredSchools] = useState<TSchool[]>([]);
  const [schoolField, setSchoolField] = useState<string>(initialSchool?.name || '');
  const [selectedSchool, setSelectedSchool] = useState<ISchool | TSchool | null>(
    initialSchool?.id ? initialSchool : null,
  );

  const debounceRef = useRef<any>();
  const initialRenderRef = useRef(true);

  useEffect(() => {
    if (!stateCode || initialRenderRef.current) {
      initialRenderRef.current = false;
      return;
    }
    setSchoolField('');
    setSelectedSchool(null);
    onChange(false, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateCode]);

  const fetchFilteredSchools = useCallback(
    async (state: string, search: string) => {
      try {
        const response = await StateCityModel.getSchoolsByState({
          state,
          search,
        });
        setFilteredSchools(response.data);
        onSuggestion?.(response.data, search);
        if (schoolField && city) {
          const school = response.data.find(
            (s: any) =>
              s.name.toLowerCase() === search?.toLowerCase().trim().replace(/ {2,}/, ' ') &&
              city?.toLowerCase() === s.city.toLowerCase(),
          );
          if (school) {
            setSelectedSchool(school);
            onChange(true, school);
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    },
    [city, onChange, schoolField, onSuggestion],
  );

  const handleSchoolChange = useCallback(
    async ({ value }: any) => {
      if (value.length < 3) {
        setFilteredSchools([]);
        return;
      }

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        fetchFilteredSchools(stateCode || '', value);
        debounceRef.current = null;
      }, 300);
    },
    [fetchFilteredSchools, stateCode],
  );

  const handleSchoolClear = () => {
    setFilteredSchools([]);
  };

  const handleClearSchoolField = () => {
    setSchoolField('');
    setSelectedSchool(null);
    onChange(false, '');
  };

  const handleChangeSchoolField = (_event: any, { newValue }: { newValue: string | TSchool }) => {
    if (typeof newValue === 'string') {
      setSchoolField(newValue);
      onChange(false, newValue);
      return;
    }
    setSelectedSchool(newValue);
    setSchoolField(newValue.name);
    onChange(true, newValue);
  };

  return (
    <div className={clsx(styles.schoolPickerContainer, className)}>
      <AutosuggestComponent
        suggestions={filteredSchools}
        onSuggestionsFetchRequested={handleSchoolChange}
        onSuggestionsClearRequested={handleSchoolClear}
        getSuggestionValue={(s: any) => s}
        renderSuggestion={optionRender}
        containerProps={{
          className: clsx(styles.autosuggest, autosuggestClassName),
        }}
        inputProps={{
          name: fieldName,
          className: clsx(styles.input, inputClassName),
          placeholder,
          disabled: disabled || selectedSchool,
          value: schoolField,
          onChange: handleChangeSchoolField,
          onBlur,
          title: schoolField,
        }}
      />
      {selectedSchool && (
        <button
          type="button"
          aria-label="cancel"
          className={styles['button-cancel']}
          onClick={handleClearSchoolField}
        >
          <span />
        </button>
      )}
    </div>
  );
};

export default SchoolPicker;
